import { LocationService } from '@peoplepower/webcore/dist/types/data/services/locationService';
import { inject, injectable } from '../../modules/common/di';
import { ApiResponseBase } from '../models/apiResponseBase';
import { UsersAndLocationsApi } from '../api/admin/usersAndLocations/usersAndLocationsApi';
import { EditLocationInOrganizationModel } from '../api/admin/usersAndLocations/editLocationInOrganizationApiResponse';
import {
  CreateLocationInOrganizationApiResponse,
  CreateLocationInOrganizationModel
} from '../api/admin/usersAndLocations/createLocationInOrganizationApiResponse';
import { AddOrUpdateOrRemoveLocationsModel } from '../api/admin/usersAndLocations/addOrUpdateOrRemoveLocationsApiResponse';
import { GetLocationTimeSeriesStatesApiResponse } from '../api/admin/usersAndLocations/getLocationTimeSeriesStatesApiResponse';
import { LocationPriorityCategory } from '@peoplepower/webcore/dist/types/data/api/app/userAccounts/getUserInformationApiResponse';

@injectable('LocationAdminService')
export class LocationAdminService extends LocationService {
  @inject('UsersAndLocationsApi') protected readonly usersAndLocationsApi!: UsersAndLocationsApi;

  constructor() {
    super();
  }

  /**
   * Updates location as Admin with new location properties values.
   * Method for admins only
   * @param {number} organizationId
   * @param {number} locationId
   * @param {EditLocationInOrganizationModel} location
   * @returns {Promise<ApiResponseBase>}
   */
  public updateLocationInOrganization(
    organizationId: number,
    locationId: number,
    location: EditLocationInOrganizationModel,
  ): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated().then(() => {
      return this.usersAndLocationsApi.editLocationInOrganization(organizationId, location, {locationId: locationId});
    });
  }

  /**
   * Create new location in organization.
   * Method for admins only
   * @param {number} organizationId
   * @param {CreateLocationInOrganizationModel} locationModel
   * @param {{parentId: number, userId: number}} [params]
   * @returns {Promise<CreateLocationInOrganizationApiResponse>}
   */
  public createLocationInOrganization(
    organizationId: number,
    locationModel: CreateLocationInOrganizationModel,
    params?: {
      parentId?: number;
      userId?: number;
    },
  ): Promise<CreateLocationInOrganizationApiResponse> {
    return this.authService.ensureAuthenticated().then(() => {
      return this.usersAndLocationsApi.createLocationInOrganization(organizationId, locationModel, params);
    });
  }

  /**
   * Add or Update or Remove Locations at Organization
   * @param {number} organizationId
   * @param {AddOrUpdateOrRemoveLocationsModel} locationsModel
   * @returns {Promise<ApiResponseBase>}
   */
  public manageMultipleLocations(organizationId: number, locationsModel: AddOrUpdateOrRemoveLocationsModel): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated().then(() => {
      return this.usersAndLocationsApi.addOrUpdateOrRemoveLocations(organizationId, locationsModel);
    });
  }

  /**
   * Delete location from organization. Method for admins only.
   * @param {number} organizationId ID of the organization to delete location for.
   * @param params Request parameters.
   * @param {number} params.locationId Id of the location to delete.
   * @returns {Promise<ApiResponseBase>}
   */
  public deleteLocationInOrganization(
    organizationId: number,
    params: {
      locationId: number;
    },
  ): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated().then(() => {
      return this.usersAndLocationsApi.deleteLocationInOrganization(organizationId, params);
    });
  }

  /**
   * Get location time-series states for an organization.
   * @param organizationId Organization ID.
   * @param startDate Start date. Can be either timestamp or ISO string.
   * @param endDate End date. Can be either timestamp or ISO string.
   * @param params Request parameters.
   * @param {number|number[]} [params.locationId] Location ID or array of location IDs to filter by.
   * @param {string|string[]} [params.name] State name(s), multiple values supported.
   * @param {LocationPriorityCategory|LocationPriorityCategory[]} [params.priorityCategory] Location priority category or array of location priority categories to filter by.
   * @param {string} [params.sortBy] Field name to sort by.
   * @param {'asc'|'desc'} [params.sortOrder] Sort order, default is ascending.
   * @returns {Promise<GetLocationTimeSeriesStatesApiResponse>}
   */
  public getLocationTimeSeriesStates(
    organizationId: number,
    startDate: string | number,
    endDate: string | number,
    params?: {
      locationId?: number | number[];
      name?: string | string[];
      priorityCategory?: LocationPriorityCategory | LocationPriorityCategory[];
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
  ): Promise<GetLocationTimeSeriesStatesApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    const parameters = {
      ...params,
      organizationId,
      startDate,
      endDate,
      sortCollection: 'states',
      sortBy: params?.sortBy || 'stateDateMs',
      sortOrder: params?.sortOrder || 'desc',
    };

    return this.authService.ensureAuthenticated().then(() => {
      return this.usersAndLocationsApi.getLocationTimeSeriesStates(parameters);
    });
  }
}
